import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtDate, fmtPercent } from "./formatters";

interface MeetingRow {
  meetingId: string;
  meetingTitle: string;
  meetingDate: string;
  type: string;
  status: string;
  organizerName: string | null;
  totalInvited: number;
  totalAttended: number;
  attendanceRate: number;
}

interface MeetingAttendanceData {
  data: MeetingRow[];
  summary: {
    totalMeetings: number;
    avgAttendanceRate: number;
    mostAttended: { title: string; rate: number } | null;
    leastAttended: { title: string; rate: number } | null;
  };
}

export function MeetingAttendanceReport({ data }: { data: MeetingAttendanceData }) {
  const cols: Array<TableColumn<MeetingRow>> = [
    {
      key: "meetingTitle",
      label: "Meeting",
      render: (r) => (
        <div>
          <div className="font-medium text-foreground">{r.meetingTitle}</div>
          <div className="text-[11px] text-muted-foreground capitalize">
            {r.type} · {r.status}
          </div>
        </div>
      ),
    },
    {
      key: "meetingDate",
      label: "Date",
      render: (r) => fmtDate(r.meetingDate),
    },
    {
      key: "organizerName",
      label: "Organizer",
      render: (r) => r.organizerName ?? "—",
    },
    { key: "totalInvited", label: "Invited", align: "right" },
    { key: "totalAttended", label: "Attended", align: "right" },
    {
      key: "attendanceRate",
      label: "Rate",
      align: "right",
      render: (r) => fmtPercent(r.attendanceRate),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Total Meetings" value={data.summary.totalMeetings} />
        <StatCard
          label="Avg Attendance Rate"
          value={fmtPercent(data.summary.avgAttendanceRate)}
        />
        <StatCard
          label="Most Attended"
          value={data.summary.mostAttended?.title ?? "—"}
          hint={
            data.summary.mostAttended
              ? fmtPercent(data.summary.mostAttended.rate)
              : undefined
          }
          tone="good"
        />
        <StatCard
          label="Least Attended"
          value={data.summary.leastAttended?.title ?? "—"}
          hint={
            data.summary.leastAttended
              ? fmtPercent(data.summary.leastAttended.rate)
              : undefined
          }
          tone="bad"
        />
      </div>

      <ReportSection title="Meetings">
        <ReportTable columns={cols} rows={data.data} rowKey={(r) => r.meetingId} />
      </ReportSection>
    </div>
  );
}
